import DataWorkspaceComingSoon from "./components/DataWorkspaceComingSoon";
import { DATA_WORKSPACE_ENABLED } from "./config/dataWorkspaceFeature";
import { useState, useEffect, lazy, Suspense } from "react";
import { Route, Routes, Navigate } from "react-router-dom";
import { CssBaseline, ThemeProvider, Box } from "@mui/material";
import { ColorModeContext, useMode } from "./theme.js";
import { PermissionAlertProvider } from "./contexts/PermissionAlertContext.jsx";
import { setLanguageFromStoredNationality } from "./utils/languageFromNationality.js";
import DoctorLayout from "./layouts/DoctorLayout.jsx";
import AiAssistantWidget from "./components/AiAssistantWidget.jsx";
// eslint-disable-next-line no-unused-vars
import PublicNotFound from "./scenes/notFoundPages/PublicNotFound.jsx";
import PublicRouteGuard from "./components/PublicRouteGuard.jsx";
import TenantRouteGuard from "./components/TenantRouteGuard.jsx";
import ErrorBoundary from "./components/ErrorBoundary.jsx";
import SessionTimeoutWarning from "./components/SessionTimeoutWarning.jsx";
import CookieConsentCMP from "./components/legal/CookieConsentCMP.jsx";
import TermsConsentGuard from "./components/legal/TermsConsentGuard.jsx";
import { purgeLegacyJwtFromStorage } from "./utils/authSession.js";

// Marketing & Public Pages
const Marketing = lazy(() => import("./scenes/marketing/index.jsx"));
const Pricing = lazy(() => import("./scenes/pricing/index.jsx"));
const Packages = lazy(() => import("./scenes/packages/index.jsx"));
const About = lazy(() => import("./scenes/about/index.jsx"));
const Contact = lazy(() => import("./scenes/contact/index.jsx"));
const TenantRegister = lazy(() => import("./scenes/register/TenantRegister.jsx"));
const IndividualRegister = lazy(() => import("./scenes/register/IndividualRegister.jsx"));
const CorporateLogin = lazy(() => import("./scenes/loginn/CorporateLogin.jsx"));
const IndividualLogin = lazy(() => import("./scenes/loginn/IndividualLogin.jsx"));
const VerifyEmailPending = lazy(() => import("./scenes/email-verification/VerifyEmailPending.jsx"));
const VerifyEmail = lazy(() => import("./scenes/email-verification/VerifyEmail.jsx"));
const PaymentSuccess = lazy(() => import("./scenes/subscription/PaymentSuccess.jsx"));
const PaymentFail = lazy(() => import("./scenes/subscription/PaymentFail.jsx"));
const PublicFormPage = lazy(() => import("./scenes/publicFormPage/PublicFormPage.jsx"));
const KioskFormFill = lazy(() => import("./scenes/kioskFormFill/KioskFormFill.jsx"));
const DoctorInvitationComplete = lazy(() => import("./scenes/doctor-invitation/DoctorInvitationComplete.jsx"));
const PrivacySecurity = lazy(() => import("./scenes/privacy-security/index.jsx"));

// Tenant Pages
const TenantCheckoutPaytr = lazy(() => import("./scenes/subscription/TenantCheckoutPaytr.jsx"));
const TenDashboard = lazy(() => import("./scenes/dashboard/TenDashboard.jsx"));
const TenantProfile = lazy(() => import("./scenes/profile/tenantProfile.jsx"));
const TenantSubscription = lazy(() => import("./scenes/subscription/TenantSubscription.jsx"));
const TenantDoctors = lazy(() => import("./scenes/tenant-doctors/index.jsx"));
const TenantPatients = lazy(() => import("./scenes/tenant-patients/index.jsx"));
const TenantsDoctorProfile = lazy(() => import("./scenes/profile/tenantsDoctorProfile.jsx"));
const TenantsPatientProfile = lazy(() => import("./scenes/profile/TenantsPatientProfile.jsx"));
const InviteAdminDoctor = lazy(() => import("./scenes/tenant-invite-doctor/InviteAdminDoctor.jsx"));
const PermissionGroupsList = lazy(() => import("./scenes/tenant-permission-groups/PermissionGroupsList.jsx"));
const CreatePermissionGroup = lazy(() => import("./scenes/tenant-permission-groups/CreatePermissionGroup.jsx"));
const ViewPermissionGroup = lazy(() => import("./scenes/tenant-permission-groups/ViewPermissionGroup.jsx"));
const TenantNotFound = lazy(() => import("./scenes/notFoundPages/TenantNotFound.jsx"));

// Password Reset Pages
const DoctorForgotPassword = lazy(() => import("./scenes/password-reset/DoctorForgotPassword.jsx"));
const DoctorVerifyOtp = lazy(() => import("./scenes/password-reset/DoctorVerifyOtp.jsx"));
const DoctorResetPassword = lazy(() => import("./scenes/password-reset/DoctorResetPassword.jsx"));
const TenantForgotPassword = lazy(() => import("./scenes/password-reset/TenantForgotPassword.jsx"));
const TenantVerifyOtp = lazy(() => import("./scenes/password-reset/TenantVerifyOtp.jsx"));
const TenantResetPassword = lazy(() => import("./scenes/password-reset/TenantResetPassword.jsx"));

// 2FA (opsiyonel e-posta tabanlı 2 Adımlı Doğrulama)
const DoctorVerify2fa = lazy(() => import("./scenes/two-factor/DoctorVerify2fa.jsx"));
const TenantVerify2fa = lazy(() => import("./scenes/two-factor/TenantVerify2fa.jsx"));

// Super Admin Pages
const SuperAdminLogin = lazy(() => import("./scenes/SüperAdminScenes/login/SuperAdminLogin.jsx"));
const SuperAdminVerify2fa = lazy(() => import("./scenes/SüperAdminScenes/login/SuperAdminVerify2fa.jsx"));
const SuperAdminLayout = lazy(() => import("./scenes/SüperAdminScenes/global/SuperAdminLayout.jsx"));
const SuperAdminDashboard = lazy(() => import("./scenes/SüperAdminScenes/dashboard/SuperAdminDashboard.jsx"));
const TenantsPage = lazy(() => import("./scenes/SüperAdminScenes/tenants/index.jsx"));
const SuperAdminTenantProfile = lazy(() => import("./scenes/SüperAdminScenes/tenant-profile/TenantProfile.jsx"));
const SuperAdminDoctorProfile = lazy(() => import("./scenes/SüperAdminScenes/doctor-profile/DoctorProfile.jsx"));

const DoctorsPage = lazy(() => import("./scenes/SüperAdminScenes/doctors/index.jsx"));
const PatientsPage = lazy(() => import("./scenes/SüperAdminScenes/patients/index.jsx"));
const AdminFormMarket = lazy(() => import("./scenes/SüperAdminScenes/form-market/index.jsx"));
const FormsPage = lazy(() => import("./scenes/SüperAdminScenes/forms/index.jsx"));
const FormAnswersPage = lazy(() => import("./scenes/SüperAdminScenes/form-answers/index.jsx"));
const FormInvitationsPage = lazy(() => import("./scenes/SüperAdminScenes/form-invitations/index.jsx"));
const ScoringFormulasPage = lazy(() => import("./scenes/SüperAdminScenes/scoring-formulas/index.jsx"));
const FilesPage = lazy(() => import("./scenes/SüperAdminScenes/files/index.jsx"));
const DoctorInvitationsPage = lazy(() => import("./scenes/SüperAdminScenes/doctor-invitations/index.jsx"));
const PasswordResetsPage = lazy(() => import("./scenes/SüperAdminScenes/password-resets/index.jsx"));
const TrashLogsPage = lazy(() => import("./scenes/SüperAdminScenes/logs/index.jsx"));
const SuperAdminPlans = lazy(() => import("./scenes/SüperAdminScenes/plans/index.jsx"));
const MonitoringModule = lazy(() => import("./scenes/SüperAdminScenes/monitoring/MonitoringModule.jsx"));

const Dashboard = lazy(() => import("./scenes/dashboard/index.jsx"));
const Team = lazy(() => import("./scenes/team/index.jsx"));
const TeamStatistics = lazy(() => import("./scenes/team/TeamStatistics.jsx"));
const Bar = lazy(() => import("./scenes/bar/index.jsx"));
const Li̇ne = lazy(() => import("./scenes/line/index.jsx"));
const Faq = lazy(() => import("./scenes/faq/index.jsx"));
const ExcelImportPage = lazy(() => import("./scenes/excel-import/index.jsx"));
const Calendar = lazy(() => import("./scenes/calendar/index.jsx"));
const FilesStatistics = lazy(() => import("./scenes/files/FilesStatistics.jsx"));
const AddPatient = lazy(() => import("./scenes/addpatient/index.jsx"));
const CreateNewFile = lazy(() => import("./scenes/createFile/index.jsx"));
const CreateNewForm = lazy(() => import("./scenes/createForm/index.jsx"));
const FormMarketPage = lazy(() => import("./scenes/form-market/index.jsx"));
const FormMarketDetail = lazy(() => import("./scenes/form-market/FormMarketDetail.jsx"));
const Profile = lazy(() => import("./scenes/profile/doctorProfile.jsx"));
const DoctorDoctorProfile = lazy(() => import("./scenes/profile/DoctorDoctorProfile.jsx"));
const File = lazy(() => import("./scenes/file/File.jsx"));
const PatientProfile = lazy(() => import("./scenes/profile/PatientProfile.jsx"));
const PatientStatistics = lazy(() => import("./scenes/profile/PatientStatistics.jsx"));
const PatientsStatistics = lazy(() => import("./scenes/patients/PatientsStatistics.jsx"));
const FormInfo = lazy(() => import("./scenes/formInfo/FormInfo.jsx"));
const FormUEdit = lazy(() => import("./scenes/formEdit/FormEdit.jsx"));
const FormFillOut = lazy(() => import("./scenes/formFillOut/FormFillOut.jsx"));
const Analyze = lazy(() => import("./scenes/analyze/Analyze.jsx"));
const FilledFormInfo = lazy(() => import("./scenes/filledFormInfo/FilledFormInfo.jsx"));
const InviteDoctor = lazy(() => import("./scenes/invite-doctor/InviteDoctor.jsx"));
const Formulas = lazy(() => import("./scenes/formulas/index.jsx"));
const CreateFormula = lazy(() => import("./scenes/formulas/CreateFormula.jsx"));
const ViewFormula = lazy(() => import("./scenes/formulas/ViewFormula.jsx"));
const EditFormula = lazy(() => import("./scenes/formulas/EditFormula.jsx"));
const DoctorPermissionGroupsList = lazy(() => import("./scenes/doctor-permission-groups/PermissionGroupsList.jsx"));
const DoctorCreatePermissionGroup = lazy(() => import("./scenes/doctor-permission-groups/CreatePermissionGroup.jsx"));
const DoctorViewPermissionGroup = lazy(() => import("./scenes/doctor-permission-groups/ViewPermissionGroup.jsx"));
const CollaborationGroupsList = lazy(() => import("./scenes/collaboration-groups/CollaborationGroupsList.jsx"));
const CreateCollaborationGroup = lazy(() => import("./scenes/collaboration-groups/CreateCollaborationGroup.jsx"));
const ViewCollaborationGroup = lazy(() => import("./scenes/collaboration-groups/ViewCollaborationGroup.jsx"));
const JobsHistoryPage = lazy(() => import("./scenes/jobs-history/JobsHistoryPage.jsx"));
const DoctorNotFound = lazy(() => import("./scenes/notFoundPages/DoctorNotFound.jsx"));
const Patients = lazy(() => import("./scenes/patients/index.jsx"));
const Files = lazy(() => import("./scenes/files/index.jsx"));
const StatisticalAnalysisPage = lazy(() => import("./scenes/statistical-analysis/index.jsx"));
const StatsAuditPreview = lazy(() => import("./scenes/statistical-analysis/components/StatsAuditPreview.jsx"));
const MachineLearningPage = lazy(() => import("./scenes/machine-learning/index.jsx"));
const FeatureImpactPage = lazy(() => import("./scenes/feature-impact/index.jsx"));
const MediaVisionPage = lazy(() => import("./scenes/mediavision/index.jsx"));
const DataWorkspacePage = lazy(() => import("./scenes/data-workspace/index.jsx"));

const ENABLE_PRICING = process.env.REACT_APP_ENABLE_PRICING === "true";

function App() {
  const [theme, colorMode] = useMode();
  const [darkMode, setDarkMode] = useState(false);

  // Sayfa yüklendiğinde nationality'ye göre dil ayarla
  useEffect(() => {
    purgeLegacyJwtFromStorage();
    setLanguageFromStoredNationality();
  }, []);

  return (
    <ColorModeContext.Provider value={colorMode}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <PermissionAlertProvider>
        <ErrorBoundary>
        <TermsConsentGuard>
        <div className="app">
          <SessionTimeoutWarning />
          <Suspense fallback={<Box sx={{ minHeight: "100vh", width: "100vw", bgcolor: "#ffffff" }} />}>
            <Routes>
              {/* Başlangıçta Marketing sayfasına yönlendirme */}
            <Route 
              path="/" 
              element={
                <Box sx={{ height: "100vh", width: "100%", maxWidth: "100vw", minWidth: 0, display: "flex", flexDirection: "column", overflowX: "clip", overflowY: "auto" }}>
                  <Marketing darkMode={darkMode} setDarkMode={setDarkMode} />
                </Box>
              } 
            />      
            <Route path="/login" element={<Navigate to="/individual-login" replace />} />
            <Route path="/individual-login" element={
              <Box sx={{ height: "100vh", width: "100%", maxWidth: "100vw", minWidth: 0, display: "flex", flexDirection: "column", overflowX: "clip", overflowY: "auto" }}>
                <IndividualLogin darkMode={darkMode} setDarkMode={setDarkMode} />
              </Box>
            } />
            <Route path="/corporate-login" element={
              <Box sx={{ height: "100vh", width: "100%", maxWidth: "100vw", minWidth: 0, display: "flex", flexDirection: "column", overflowX: "clip", overflowY: "auto" }}>
                <CorporateLogin darkMode={darkMode} setDarkMode={setDarkMode} />
              </Box>
            } />
            <Route path="/about" element={
              <Box sx={{ height: "100vh", width: "100%", maxWidth: "100vw", minWidth: 0, display: "flex", flexDirection: "column", overflowX: "clip", overflowY: "auto" }}>
                <About darkMode={darkMode} setDarkMode={setDarkMode} />
              </Box>
            } />
            <Route path="/contact" element={
              <Box sx={{ height: "100vh", width: "100%", maxWidth: "100vw", minWidth: 0, display: "flex", flexDirection: "column", overflowX: "clip", overflowY: "auto" }}>
                <Contact darkMode={darkMode} setDarkMode={setDarkMode} />
              </Box>
            } />
            <Route path="/gizlilik-ve-guvenlik" element={
              <Box sx={{ height: "100vh", width: "100%", maxWidth: "100vw", minWidth: 0, display: "flex", flexDirection: "column", overflowX: "clip", overflowY: "auto" }}>
                <PrivacySecurity darkMode={darkMode} setDarkMode={setDarkMode} />
              </Box>
            } />
            <Route path="/gizlilik-ve-guvenlik/:slug" element={
              <Box sx={{ height: "100vh", width: "100%", maxWidth: "100vw", minWidth: 0, display: "flex", flexDirection: "column", overflowX: "clip", overflowY: "auto" }}>
                <PrivacySecurity darkMode={darkMode} setDarkMode={setDarkMode} />
              </Box>
            } />
            <Route path="/meeting" element={
              <Box sx={{ height: "100vh", width: "100%", maxWidth: "100vw", minWidth: 0, display: "flex", flexDirection: "column", overflowX: "clip", overflowY: "auto" }}>
                <Pricing darkMode={darkMode} setDarkMode={setDarkMode} />
              </Box>
            } />
            <Route path="/packages" element={
              !ENABLE_PRICING ? (
                <Navigate to="/contact" replace />
              ) : (
                <Box sx={{ height: "100vh", width: "100%", maxWidth: "100vw", minWidth: 0, display: "flex", flexDirection: "column", overflowX: "clip", overflowY: "auto" }}>
                  <Packages darkMode={darkMode} setDarkMode={setDarkMode} />
                </Box>
              )
            } />
            <Route path="/tenantRegister" element={
              <Box sx={{ height: "100vh", width: "100%", maxWidth: "100vw", minWidth: 0, display: "flex", flexDirection: "column", overflowX: "clip", overflowY: "auto" }}>
                <TenantRegister darkMode={darkMode} setDarkMode={setDarkMode} />
              </Box>
            } />
            <Route path="/register-individual" element={
              <Box sx={{ height: "100vh", width: "100%", maxWidth: "100vw", minWidth: 0, display: "flex", flexDirection: "column", overflowX: "clip", overflowY: "auto" }}>
                <IndividualRegister darkMode={darkMode} setDarkMode={setDarkMode} />
              </Box>
            } />
            <Route path="/verify-email-pending" element={
              <Box sx={{ height: "100vh", width: "100%", maxWidth: "100vw", minWidth: 0, display: "flex", flexDirection: "column", overflowX: "clip", overflowY: "auto" }}>
                <VerifyEmailPending darkMode={darkMode} setDarkMode={setDarkMode} />
              </Box>
            } />
            <Route path="/verify-email" element={
              <Box sx={{ height: "100vh", width: "100%", maxWidth: "100vw", minWidth: 0, display: "flex", flexDirection: "column", overflowX: "clip", overflowY: "auto" }}>
                <VerifyEmail darkMode={darkMode} setDarkMode={setDarkMode} />
              </Box>
            } />
            <Route path="/payment-success" element={
              <Box sx={{ height: "100vh", width: "100%", maxWidth: "100vw", minWidth: 0, display: "flex", flexDirection: "column", overflowX: "clip", overflowY: "auto" }}>
                <PaymentSuccess darkMode={darkMode} setDarkMode={setDarkMode} />
              </Box>
            } />
            <Route path="/payment-fail" element={
              <Box sx={{ height: "100vh", width: "100%", maxWidth: "100vw", minWidth: 0, display: "flex", flexDirection: "column", overflowX: "clip", overflowY: "auto" }}>
                <PaymentFail darkMode={darkMode} setDarkMode={setDarkMode} />
              </Box>
            } />
            <Route path="/tenant/checkout" element={
              <TenantRouteGuard>
                <TenantCheckoutPaytr />
              </TenantRouteGuard>
            } />
            <Route path="/tenant/dashboard" element={
              <TenantRouteGuard>
                <TenDashboard darkMode={darkMode} setDarkMode={setDarkMode} />
              </TenantRouteGuard>
            } />
            <Route path="/tenant/profile" element={
              <TenantRouteGuard>
                <TenantProfile/>
              </TenantRouteGuard>
            }/>
            <Route path="/tenant/subscription/change" element={<Navigate to="/tenant/subscription" replace />} />
            <Route path="/tenant/subscription" element={
              <TenantRouteGuard>
                <TenantSubscription />
              </TenantRouteGuard>
            }/>
            <Route path="/tenant/doctors" element={
              <TenantRouteGuard>
                <TenantDoctors/>
              </TenantRouteGuard>
            }/>
            <Route path="/tenant/patients" element={
              <TenantRouteGuard>
                <TenantPatients/>
              </TenantRouteGuard>
            }/>
            <Route path="/tenant/doctors/:id" element={
              <TenantRouteGuard>
                <TenantsDoctorProfile/>
              </TenantRouteGuard>
            }/>
            <Route path="/tenant/patients/:id" element={
              <TenantRouteGuard>
                <TenantsPatientProfile/>
              </TenantRouteGuard>
            }/>
            <Route path="/tenant/permission-groups" element={
              <TenantRouteGuard>
                <PermissionGroupsList/>
              </TenantRouteGuard>
            }/>
            <Route path="/tenant/permission-groups/create" element={
              <TenantRouteGuard>
                <CreatePermissionGroup/>
              </TenantRouteGuard>
            }/>
            <Route path="/tenant/permission-groups/edit/:id" element={
              <TenantRouteGuard>
                <CreatePermissionGroup/>
              </TenantRouteGuard>
            }/>
            <Route path="/tenant/permission-groups/:id" element={
              <TenantRouteGuard>
                <ViewPermissionGroup/>
              </TenantRouteGuard>
            }/>
            <Route path="/tenant/collaboration-groups" element={
              <TenantRouteGuard>
                <CollaborationGroupsList tenantMode />
              </TenantRouteGuard>
            }/>
            <Route path="/tenant/collaboration-groups/create" element={
              <TenantRouteGuard>
                <CreateCollaborationGroup tenantMode />
              </TenantRouteGuard>
            }/>
            <Route path="/tenant/collaboration-groups/edit/:id" element={
              <TenantRouteGuard>
                <CreateCollaborationGroup tenantMode />
              </TenantRouteGuard>
            }/>
            <Route path="/tenant/collaboration-groups/:id" element={
              <TenantRouteGuard>
                <ViewCollaborationGroup tenantMode />
              </TenantRouteGuard>
            }/>
            {/* Tenant 404 - Catch all unmatched /tenant/* routes */}
            <Route path="/tenant/*" element={
              <TenantRouteGuard>
                <TenantNotFound />
              </TenantRouteGuard>
            } />
            <Route path="/api/public/forms/:token" element={<PublicFormPage />} />
            <Route
              path="/kiosk/forms/:formId/patient/:patientId"
              element={
                <PublicRouteGuard>
                  <Suspense fallback={null}>
                    <KioskFormFill />
                  </Suspense>
                </PublicRouteGuard>
              }
            />
            
            {/* 🆕 Doctor Invitation Routes */}
            <Route path="/invite/doctor/:token" element={
              <Box sx={{ height: "100vh", width: "100%", maxWidth: "100vw", minWidth: 0, display: "flex", flexDirection: "column", overflowX: "clip", overflowY: "auto" }}>
                <DoctorInvitationComplete darkMode={darkMode} setDarkMode={setDarkMode} />
              </Box>
            } />
            
            {/* Tenant Routes */}
            <Route path="/tenant/invite-admin-doctor" element={
              <TenantRouteGuard>
                <InviteAdminDoctor />
              </TenantRouteGuard>
            } />
            
            {/* Password Reset Routes - Doctor */}
            <Route path="/forgot-password/doctor" element={
              <Box sx={{ height: "100vh", width: "100%", maxWidth: "100vw", minWidth: 0, display: "flex", flexDirection: "column", overflowX: "clip", overflowY: "auto" }}>
                <DoctorForgotPassword darkMode={darkMode} setDarkMode={setDarkMode} />
              </Box>
            } />
            <Route path="/verify-otp/doctor" element={
              <Box sx={{ height: "100vh", width: "100%", maxWidth: "100vw", minWidth: 0, display: "flex", flexDirection: "column", overflowX: "clip", overflowY: "auto" }}>
                <DoctorVerifyOtp darkMode={darkMode} setDarkMode={setDarkMode} />
              </Box>
            } />
            <Route path="/reset-password/doctor" element={
              <Box sx={{ height: "100vh", width: "100%", maxWidth: "100vw", minWidth: 0, display: "flex", flexDirection: "column", overflowX: "clip", overflowY: "auto" }}>
                <DoctorResetPassword darkMode={darkMode} setDarkMode={setDarkMode} />
              </Box>
            } />
            
            {/* Password Reset Routes - Tenant */}
            <Route path="/forgot-password/tenant" element={
              <Box sx={{ height: "100vh", width: "100%", maxWidth: "100vw", minWidth: 0, display: "flex", flexDirection: "column", overflowX: "clip", overflowY: "auto" }}>
                <TenantForgotPassword darkMode={darkMode} setDarkMode={setDarkMode} />
              </Box>
            } />
            <Route path="/verify-otp/tenant" element={
              <Box sx={{ height: "100vh", width: "100%", maxWidth: "100vw", minWidth: 0, display: "flex", flexDirection: "column", overflowX: "clip", overflowY: "auto" }}>
                <TenantVerifyOtp darkMode={darkMode} setDarkMode={setDarkMode} />
              </Box>
            } />
            <Route path="/reset-password/tenant" element={
              <Box sx={{ height: "100vh", width: "100%", maxWidth: "100vw", minWidth: 0, display: "flex", flexDirection: "column", overflowX: "clip", overflowY: "auto" }}>
                <TenantResetPassword darkMode={darkMode} setDarkMode={setDarkMode} />
              </Box>
            } />

            {/* 2FA (opsiyonel e-posta tabanlı 2 Adımlı Doğrulama) - login sırasında OTP doğrulama */}
            <Route path="/verify-2fa/doctor" element={
              <Box sx={{ height: "100vh", width: "100%", maxWidth: "100vw", minWidth: 0, display: "flex", flexDirection: "column", overflowX: "clip", overflowY: "auto" }}>
                <DoctorVerify2fa darkMode={darkMode} setDarkMode={setDarkMode} />
              </Box>
            } />
            <Route path="/verify-2fa/tenant" element={
              <Box sx={{ height: "100vh", width: "100%", maxWidth: "100vw", minWidth: 0, display: "flex", flexDirection: "column", overflowX: "clip", overflowY: "auto" }}>
                <TenantVerify2fa darkMode={darkMode} setDarkMode={setDarkMode} />
              </Box>
            } />
            
            {/* Super Admin Routes */}
            <Route path="/super-admin/auth/secure-access/login" element={<SuperAdminLogin />} />
            <Route path="/super-admin/verify-2fa" element={<SuperAdminVerify2fa />} />
            <Route
              path="/super-admin/*"
              element={
                <SuperAdminLayout>
                  <Routes>
                    <Route path="dashboard" element={<SuperAdminDashboard />} />
                    <Route path="plans" element={<SuperAdminPlans />} />
                    <Route path="billing" element={<Navigate to="/super-admin/plans" replace />} />
                    <Route path="tenants" element={<TenantsPage />} />
            <Route path="tenants/:id" element={<SuperAdminTenantProfile />} />
            <Route path="doctors/:id" element={<SuperAdminDoctorProfile />} />

                    <Route path="doctors" element={<DoctorsPage />} />
                    <Route path="patients" element={<PatientsPage />} />
                    <Route path="forms" element={<FormsPage />} />
                    <Route path="form-market" element={<AdminFormMarket />} />
                    <Route path="form-answers" element={<FormAnswersPage />} />
                    <Route path="form-invitations" element={<FormInvitationsPage />} />
                    <Route path="scoring-formulas" element={<ScoringFormulasPage />} />
                    <Route path="files" element={<FilesPage />} />
                    <Route path="doctor-invitations" element={<DoctorInvitationsPage />} />
                    <Route path="password-resets" element={<PasswordResetsPage />} />
                    <Route path="trash-logs" element={<TrashLogsPage />} />
                    <Route path="monitoring/*" element={<MonitoringModule />} />
                  </Routes>
                </SuperAdminLayout>
              }
            />
            
            <Route
              path="/*"
              element={
                <PublicRouteGuard>
                  <DoctorLayout>
                    <AiAssistantWidget />
                    <Suspense fallback={null}>
                      <Routes>
                        <Route path="/dashboard" element={<Dashboard />} />
                        <Route path="/subscription" element={<TenantSubscription disableLayout={true} />} />
                        <Route path="/team" element={<Team />} />
                        <Route path="/team/statistics" element={<TeamStatistics />} />
                        <Route path="/bar" element={<Bar />} />
                        <Route path="/patients" element={<Patients />} />
                        <Route path="/faq" element={<Faq />} />
                        {/* 🆕 Admin Doctor Invite Route */}
                        <Route path="/inviteDoctor" element={<InviteDoctor />} />
                        {/* 🧮 Scoring Formulas Routes */}
                        <Route path="/form-market" element={<FormMarketPage />} />
                        <Route path="/form-market/:slug" element={<FormMarketDetail />} />
                        <Route path="/formulas" element={<Formulas />} />
                        <Route path="/formulas/create" element={<CreateFormula />} />
                        <Route path="/formulas/edit/:id" element={<EditFormula />} />
                        <Route path="/formulas/:id" element={<ViewFormula />} />
                        <Route path="/calendar" element={<Calendar />} />
                        <Route path="/files" element={<Files />} />
                        <Route path="/excel-import" element={<ExcelImportPage />} />
                        <Route path="/files/statistics" element={<FilesStatistics />} />
                        <Route path="/statistical-analysis" element={<StatisticalAnalysisPage />} />
                        <Route path="/doctor/statistical-analysis" element={<StatisticalAnalysisPage />} />
                        <Route path="/stats-audit-preview" element={<StatsAuditPreview />} />
                        <Route path="/machine-learning" element={<MachineLearningPage />} />
                        <Route path="/doctor/machine-learning" element={<MachineLearningPage />} />
                        <Route path="/media-vision" element={<MediaVisionPage />} />
                        <Route path="/doctor/media-vision" element={<MediaVisionPage />} />
                        <Route path="/feature-impact" element={<FeatureImpactPage />} />
                        <Route path="/doctor/feature-impact" element={<FeatureImpactPage />} />
                        <Route path="/analysis-history" element={<JobsHistoryPage />} />
                        <Route path="/data-workspace" element={DATA_WORKSPACE_ENABLED ? <DataWorkspacePage /> : <DataWorkspaceComingSoon />} />
                        <Route path="/data-workspace/:datasetId" element={DATA_WORKSPACE_ENABLED ? <DataWorkspacePage /> : <DataWorkspaceComingSoon />} />
                        <Route path="/doctor/data-workspace" element={DATA_WORKSPACE_ENABLED ? <DataWorkspacePage /> : <DataWorkspaceComingSoon />} />
                        <Route path="/doctor/data-workspace/:datasetId" element={DATA_WORKSPACE_ENABLED ? <DataWorkspacePage /> : <DataWorkspaceComingSoon />} />
                        {/* eslint-disable-next-line react/jsx-pascal-case */}
                        <Route path="/line" element={<Li̇ne />} />

                        <Route path="/AddPatient" element={<AddPatient />} />
                        <Route path="/Create-NewFile" element={<CreateNewFile/>} />
                        <Route path="/Create-NewForm/:id" element={<CreateNewForm/>} />
                        <Route path="/profile" element={<Profile/>} />
                        <Route path="/doctors/:id" element={<DoctorDoctorProfile/>} />
                        <Route path="/files/:id" element={<File/>} />
                        <Route path="/patients/statistics" element={<PatientsStatistics />} />
                        <Route path="/patients/:id" element={<PatientProfile/>} />
                        <Route path="/patients/:id/statistics" element={<PatientStatistics/>} />
                        <Route path="/files/:id/:formId" element={<FormInfo/>} />
                        <Route path="/files/:id/:formId/edit" element={<FormUEdit/>}/>
                        <Route path="/files/:id/:formId/fill-out" element={<FormFillOut/>}/>
                        <Route path="/patients/:id/:mrId" element={<Analyze/>} />
                        <Route path="/patients/:id/form/:formId" element={<FilledFormInfo/>} />
                        
                        {/* Doctor Permission Groups Routes */}
                        <Route path="/doctor-permission-groups" element={<DoctorPermissionGroupsList/>}/>
                        <Route path="/doctor-permission-groups/create" element={<DoctorCreatePermissionGroup/>}/>
                        <Route path="/doctor-permission-groups/edit/:id" element={<DoctorCreatePermissionGroup/>}/>
                        <Route path="/doctor-permission-groups/:id" element={<DoctorViewPermissionGroup/>}/>
                        {/* Collaboration Groups Routes */}
                        <Route path="/collaboration-groups" element={<CollaborationGroupsList/>}/>
                        <Route path="/collaboration-groups/create" element={<CreateCollaborationGroup/>}/>
                        <Route path="/collaboration-groups/edit/:id" element={<CreateCollaborationGroup/>}/>
                        <Route path="/collaboration-groups/:id" element={<ViewCollaborationGroup/>}/>
                        {/* 404 Route */}
                        <Route path="/404" element={<DoctorNotFound />} />
                        {/* Doctor 404 - Catch all unmatched routes */}
                        <Route path="*" element={<DoctorNotFound />} />
                      </Routes>
                    </Suspense>
                  </DoctorLayout>
                </PublicRouteGuard>
              }
            />
          </Routes>
          </Suspense>
        </div>
        <CookieConsentCMP />
        </TermsConsentGuard>
        </ErrorBoundary>
        </PermissionAlertProvider>
      </ThemeProvider>
    </ColorModeContext.Provider>
  );
}

export default App;
